import { useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { Search, Mail, Phone, Globe, ArrowRight, BadgeCheck, SlidersHorizontal } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { EventServiceListItem, SharedProps } from '@/types'

const SERVICE_TYPES = [
  'Photographer', 'Videographer', 'Florist', 'Venue', 'Event Planner',
  'Caterer', 'DJ/Entertainment', 'Decorator', 'Auctioneer', 'Event Experience',
  'Rental Services',
]

const PREVIEW_VENDORS = [
  { name: 'Lumen Studios', type: 'Photographer', location: 'Brooklyn, NY', price: '$$$' },
  { name: 'Fleur de Soleil', type: 'Florist', location: 'Manhattan, NY', price: '$$' },
  { name: 'Apex Catering Co.', type: 'Caterer', location: 'Queens, NY', price: '$$$' },
  { name: 'The Plaza Hotel', type: 'Venue', location: 'Midtown, NY', price: '$$$$' },
]

type Props = {
  services: EventServiceListItem[]
  filters: { search: string; service_type: string }
}

export default function EventServicesIndex({ services, filters }: Props) {
  const { app_url } = usePage<SharedProps>().props
  const ogImage = `${app_url}/meta.png`
  const [search, setSearch] = useState(filters.search)

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get('/services', { search, service_type: filters.service_type }, { preserveState: true })
  }

  function setServiceType(type: string) {
    const value = filters.service_type === type ? '' : type
    router.get('/services', { search: filters.search, service_type: value }, { preserveState: true })
  }

  return (
    <>
      <Head title="Event Services Directory">
        <meta head-key="description" name="description" content="Find trusted photographers, venues, caterers, and more for your gala and event needs." />
        <meta head-key="og:title" property="og:title" content="Event Services Directory — Gala Season" />
        <meta head-key="og:description" property="og:description" content="Find trusted photographers, venues, caterers, and more for your gala and event needs." />
        <meta head-key="og:type" property="og:type" content="website" />
        <meta head-key="og:url" property="og:url" content={`${app_url}/services`} />
        <meta head-key="og:image" property="og:image" content={ogImage} />
        <meta head-key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta head-key="twitter:title" name="twitter:title" content="Event Services Directory — Gala Season" />
        <meta head-key="twitter:description" name="twitter:description" content="Find trusted photographers, venues, caterers, and more for your gala and event needs." />
        <meta head-key="twitter:image" name="twitter:image" content={ogImage} />
      </Head>

      {/* Hero */}
      <div className="bg-background">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-2 lg:gap-12 lg:px-8">
          {/* Left column */}
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-primary">Event Services Directory</p>
            <h1 className="mt-4 max-w-[16ch] text-5xl font-semibold tracking-tight text-foreground text-balance sm:text-6xl">
              Find trusted vendors{' '}
              <em className="italic text-primary underline decoration-primary/50 decoration-2 underline-offset-4">for the night.</em>
            </h1>
            <form onSubmit={handleSearch} className="mt-8 flex max-w-md gap-2">
              <div className="relative flex-1">
                <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by business name or service"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button type="submit" className="font-semibold">
                Search <ArrowRight className="size-4" />
              </Button>
            </form>
            <p className="mt-4 text-base text-muted-foreground">
              Are you a vendor?{' '}
              <Link href="/services/submit" className="font-medium text-primary underline underline-offset-4 hover:text-primary/90">
                List your business for free
              </Link>
            </p>
            <div className="mt-8 flex gap-10">
              <div>
                <div className="text-3xl font-semibold text-foreground tabular-nums">{services.length}</div>
                <div className="mt-0.5 text-sm font-medium text-muted-foreground">Services</div>
              </div>
              <div>
                <div className="text-3xl font-semibold text-foreground tabular-nums">{new Set(services.map(s => s.service_type)).size}</div>
                <div className="mt-0.5 text-sm font-medium text-muted-foreground">Categories</div>
              </div>
            </div>
          </div>

          {/* Right column — directory preview mockup */}
          <div className="relative" aria-hidden="true">
            <div className="rounded-2xl border border-black/10 bg-card shadow-xs">
              <div className="flex items-center gap-3 border-b border-black/5 p-4">
                <div className="flex flex-1 items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                  <Search className="size-4" /> Search vendors
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-lg border border-black/10 px-3 py-2 text-sm font-medium text-foreground">
                  <SlidersHorizontal className="size-4" /> Filters
                </div>
              </div>
              <ul className="divide-y divide-black/5">
                {PREVIEW_VENDORS.map(vendor => (
                  <li key={vendor.name} className="flex items-center gap-3 p-4">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/8 text-base font-semibold text-primary">
                      {vendor.name.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-foreground">{vendor.name}</p>
                      <p className="truncate text-sm text-muted-foreground">{vendor.type} · {vendor.location}</p>
                    </div>
                    <Badge variant="secondary" className="tabular-nums">{vendor.price}</Badge>
                  </li>
                ))}
              </ul>
            </div>
            <div className="absolute -bottom-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-green-600 px-3 py-1.5 text-sm font-medium text-white shadow-md">
              <BadgeCheck className="size-4" /> Reviewed by our team
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">

        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => setServiceType('')}>
            <Badge variant={!filters.service_type ? 'default' : 'outline'}>All Services</Badge>
          </button>
          {SERVICE_TYPES.map(type => (
            <button key={type} onClick={() => setServiceType(type)}>
              <Badge variant={filters.service_type === type ? 'default' : 'outline'}>{type}</Badge>
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {services.map(service => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>

        {services.length === 0 && (
          <div className="mt-12 text-center text-muted-foreground">
            <p className="text-xl">No services found.</p>
            <p className="mt-1 text-base">Try adjusting your search or filters.</p>
          </div>
        )}

        <div className="mt-16 overflow-hidden rounded-2xl bg-linear-to-br from-primary/8 via-secondary/10 to-primary/5 ring-1 ring-primary/15 p-8 sm:p-10 text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-balance">
            Are you an event services vendor?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-lg text-foreground/80 text-pretty">
            Get in front of nonprofit event organizers planning galas across the country. Listings are free and reviewed by our team before going live.
          </p>
          <div className="mt-6">
            <Link href="/services/submit">
              <Button size="lg">List your business</Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}

function ServiceCard({ service }: { service: EventServiceListItem }) {
  const location = [service.city, service.state].filter(Boolean).join(', ')

  return (
    <Link
      href={`/services/${service.slug}`}
      className="group block rounded-2xl bg-card ring-1 ring-black/5 shadow-sm hover:shadow-lg hover:ring-primary/20 transition-all"
    >
      <div className="relative h-44 overflow-hidden rounded-t-2xl bg-muted/30">
        {service.cover_image_url ? (
          <img
            src={service.cover_image_url}
            alt={service.name}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            {service.logo_url ? (
              <img src={service.logo_url} alt={service.name} className="max-h-20 max-w-32 object-contain" />
            ) : (
              <div className="size-12 rounded-full bg-primary/8" />
            )}
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="font-semibold tracking-tight">{service.name}</h3>
        <p className="text-base text-muted-foreground">{service.service_type}</p>
        {service.price_range && (
          <Badge variant="secondary" className="mt-1">{service.price_range}</Badge>
        )}

        {service.description && (
          <p className="mt-2 text-base text-foreground line-clamp-2">{service.description}</p>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          {service.contact_email && (
            <span className="inline-flex items-center gap-1 rounded-md bg-muted/50 px-2 py-1 text-sm text-muted-foreground">
              <Mail className="size-3" /> Email
            </span>
          )}
          {service.phone && (
            <span className="inline-flex items-center gap-1 rounded-md bg-muted/50 px-2 py-1 text-sm text-muted-foreground">
              <Phone className="size-3" /> Call
            </span>
          )}
          {service.website && (
            <span className="inline-flex items-center gap-1 rounded-md bg-muted/50 px-2 py-1 text-sm text-muted-foreground">
              <Globe className="size-3" /> Website
            </span>
          )}
        </div>

        {location && (
          <p className="mt-2 text-sm text-muted-foreground">{location}</p>
        )}
      </div>
    </Link>
  )
}
